import React from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Container,
  Grid,
  Tab,
  Tabs,
  Typography,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Header, Page, Content, InfoCard } from '@backstage/core-components';
import DataUsageIcon from '@material-ui/icons/DataUsage';
import CodeIcon from '@material-ui/icons/Code';
import ApiIcon from '@material-ui/icons/Settings';
import EventIcon from '@material-ui/icons/Event';
import StorageIcon from '@material-ui/icons/Storage';
import SecurityIcon from '@material-ui/icons/Security';

const useStyles = makeStyles(theme => ({
  tabContent: {
    marginTop: theme.spacing(2),
  },
  chip: {
    margin: theme.spacing(0.5),
  },
  cardGrid: {
    marginTop: theme.spacing(2),
  },
  statCard: {
    textAlign: 'center',
    padding: theme.spacing(2),
  },
  statValue: {
    fontSize: '2.5rem',
    fontWeight: 'bold',
    color: theme.palette.primary.main,
  },
  categoryIcon: {
    fontSize: '3rem',
    color: theme.palette.primary.main,
  },
}));

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`openhmis-tabpanel-${index}`}
      aria-labelledby={`openhmis-tab-${index}`}
      {...other}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

export const OpenHMISPage = () => {
  const classes = useStyles();
  const [tabValue, setTabValue] = React.useState(0);

  const handleTabChange = (_event: React.ChangeEvent<{}>, newValue: number) => {
    setTabValue(newValue);
  };

  const datasets = [
    { name: 'COVID-19 Surveillance Data', status: 'Active', records: '2.5M+' },
    { name: 'Immunization Records', status: 'Active', records: '1.8M+' },
    { name: 'Disease Outbreak Tracking', status: 'Active', records: '500K+' },
    { name: 'Public Health Indicators', status: 'Active', records: '750K+' },
  ];

  const apis = [
    { name: 'FHIR Health Records API', version: 'R4', type: 'REST' },
    { name: 'Disease Surveillance API', version: '2.1', type: 'REST' },
    { name: 'Analytics Reporting API', version: '1.3', type: 'GraphQL' },
    { name: 'Data Export API', version: '1.0', type: 'REST' },
  ];

  const codeProjects = [
    { name: 'HMIS Data Pipeline', language: 'Python', stars: 45 },
    { name: 'Health Analytics Dashboard', language: 'React', stars: 32 },
    { name: 'Disease Tracker', language: 'Java', stars: 28 },
    { name: 'Immunization Scheduler', language: 'TypeScript', stars: 19 },
  ];

  return (
    <Page themeId="tool">
      <Header
        title="Open Health Management Information System"
        subtitle="Central hub for public health data, APIs, and open source collaboration"
      />
      <Content>
        <Container maxWidth="xl">
          <Grid container spacing={3}>
            {/* Statistics Cards */}
            <Grid item xs={12} sm={6} md={3}>
              <Card className={classes.statCard}>
                <DataUsageIcon className={classes.categoryIcon} />
                <Typography variant="h6">Datasets</Typography>
                <Typography className={classes.statValue}>42</Typography>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card className={classes.statCard}>
                <ApiIcon className={classes.categoryIcon} />
                <Typography variant="h6">APIs</Typography>
                <Typography className={classes.statValue}>18</Typography>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card className={classes.statCard}>
                <CodeIcon className={classes.categoryIcon} />
                <Typography variant="h6">Code Projects</Typography>
                <Typography className={classes.statValue}>27</Typography>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card className={classes.statCard}>
                <EventIcon className={classes.categoryIcon} />
                <Typography variant="h6">Events</Typography>
                <Typography className={classes.statValue}>15</Typography>
              </Card>
            </Grid>
          </Grid>

          {/* Tabs Section */}
          <Box mt={4}>
            <Card>
              <Tabs
                value={tabValue}
                onChange={handleTabChange}
                indicatorColor="primary"
                textColor="primary"
              >
                <Tab label="Data" icon={<StorageIcon />} />
                <Tab label="APIs" icon={<ApiIcon />} />
                <Tab label="Code" icon={<CodeIcon />} />
                <Tab label="Events" icon={<EventIcon />} />
              </Tabs>

              {/* Data Tab */}
              <TabPanel value={tabValue} index={0}>
                <Box className={classes.tabContent} p={3}>
                  <Typography variant="h5" gutterBottom>
                    Public Health Datasets
                  </Typography>
                  <Typography variant="body2" color="textSecondary" paragraph>
                    Access open health data for research, analytics, and public
                    health initiatives
                  </Typography>
                  <Grid container spacing={2} className={classes.cardGrid}>
                    {datasets.map((dataset, index) => (
                      <Grid item xs={12} md={6} key={index}>
                        <InfoCard title={dataset.name}>
                          <Box>
                            <Chip
                              label={dataset.status}
                              color="primary"
                              size="small"
                              className={classes.chip}
                            />
                            <Typography
                              variant="body2"
                              style={{ marginTop: 8 }}
                            >
                              Records: {dataset.records}
                            </Typography>
                          </Box>
                        </InfoCard>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              </TabPanel>

              {/* APIs Tab */}
              <TabPanel value={tabValue} index={1}>
                <Box className={classes.tabContent} p={3}>
                  <Typography variant="h5" gutterBottom>
                    Health Information APIs
                  </Typography>
                  <Typography variant="body2" color="textSecondary" paragraph>
                    RESTful and GraphQL APIs for integrating health data into
                    your applications
                  </Typography>
                  <Grid container spacing={2} className={classes.cardGrid}>
                    {apis.map((api, index) => (
                      <Grid item xs={12} md={6} key={index}>
                        <Card>
                          <CardHeader
                            title={api.name}
                            subheader={`Version ${api.version}`}
                          />
                          <CardContent>
                            <Chip
                              label={api.type}
                              color="secondary"
                              size="small"
                            />
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              </TabPanel>

              {/* Code Tab */}
              <TabPanel value={tabValue} index={2}>
                <Box className={classes.tabContent} p={3}>
                  <Typography variant="h5" gutterBottom>
                    Open Source Projects
                  </Typography>
                  <Typography variant="body2" color="textSecondary" paragraph>
                    Community-driven health technology projects and tools
                  </Typography>
                  <List>
                    {codeProjects.map((project, index) => (
                      <React.Fragment key={index}>
                        <ListItem>
                          <ListItemIcon>
                            <CodeIcon color="primary" />
                          </ListItemIcon>
                          <ListItemText
                            primary={project.name}
                            secondary={`${project.language} • ⭐ ${project.stars} stars`}
                          />
                        </ListItem>
                        {index < codeProjects.length - 1 && <Divider />}
                      </React.Fragment>
                    ))}
                  </List>
                </Box>
              </TabPanel>

              {/* Events Tab */}
              <TabPanel value={tabValue} index={3}>
                <Box className={classes.tabContent} p={3}>
                  <Typography variant="h5" gutterBottom>
                    Upcoming Events
                  </Typography>
                  <Typography variant="body2" color="textSecondary" paragraph>
                    Conferences, workshops, and community meetups
                  </Typography>
                  <Grid container spacing={2} className={classes.cardGrid}>
                    <Grid item xs={12} md={6}>
                      <InfoCard title="Global Health Data Summit 2026">
                        <Typography variant="body2" color="textSecondary">
                          March 15-17, 2026 • Virtual Event
                        </Typography>
                      </InfoCard>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <InfoCard title="HMIS Developer Workshop">
                        <Typography variant="body2" color="textSecondary">
                          April 8, 2026 • Online
                        </Typography>
                      </InfoCard>
                    </Grid>
                  </Grid>
                </Box>
              </TabPanel>
            </Card>
          </Box>

          {/* Security & Privacy Notice */}
          <Box mt={4}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" mb={2}>
                  <SecurityIcon style={{ marginRight: 8 }} />
                  <Typography variant="h6">Security & Privacy</Typography>
                </Box>
                <Typography variant="body2" color="textSecondary">
                  All health data is anonymized and complies with HIPAA
                  regulations. This platform contains only non-sensitive,
                  publicly available data and information. For more information
                  about our privacy policy and data handling practices, please
                  contact the system administrator.
                </Typography>
              </CardContent>
            </Card>
          </Box>
        </Container>
      </Content>
    </Page>
  );
};
